import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Formik } from 'formik';
import { first } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import routes from '@/routes/constants';
import { usePaymentContext } from '@/mp/hooks/usePaymentContext';
import { useAppSelector } from '@/hooks/useAppSelector';
import { StripeError } from '@stripe/stripe-js';
import { useGetUserCardsQuery } from '@/redux/rtk';
import useUser from '@/mp/hooks/useUser';
import text from '@/text';
import CardOption from './CardOption';
import TextDivider from '@/components/text/TextDivider';
import Loading from '@/components/loading/Loading';
import Accordion from '@/components/accordion/Accordion';
import NewCard from './NewCard';
import { Button } from '@/components/button';
import { Control } from '@/components/form';
import { setPaymentMethodId } from '@/redux/actions';

type FormValues = {
  name?: string;
  savedCardId: string | null;
  saveCard: boolean;
  discountCode?: string;
};

const PaymentMethodForm = () => {
  const stripe = useStripe();
  const elements = useElements();

  const dispatch = useAppDispatch();

  const { jobId } = usePaymentContext();

  const currentUser = useUser();
  const getUserCards = useGetUserCardsQuery();

  const paymentMethodId = useAppSelector((state) => state.order.paymentMethodId);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState<StripeError | null>(null);
  const [isNewCardDetailsComplete, setIsNewCardDetailsComplete] =
    useState<boolean>(false);
  const [currentEventKey, setCurrentEventKey] = useState<string | null>(null);

  const { returnPage, returnUrl } = useMemo(() => {
    const returnPage = routes.mp.payment(jobId);
    const returnUrl = `${process.env.LARKI_APP_URL}${returnPage}`;
    return { returnPage, returnUrl };
  }, [jobId]);

  const initialValues: FormValues = {
    name: currentUser.profile?.full_name,
    savedCardId: paymentMethodId,
    saveCard: true,
  };

  const isStripeLoading = !stripe || !elements;

  const handleSubmit = async (values: FormValues) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    if (isStripeLoading) {
      return;
    }

    try {
      setIsSubmitting(true);
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              address: {
                country: 'au',
              },
              name: values.name,
            },
          },
          return_url: returnUrl,
        },
        redirect: 'if_required',
      });
      if (error) {
        setPaymentError(error);
        throw error;
      }
      getUserCards.refetch();
      dispatch(setPaymentMethodId(setupIntent.payment_method));
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='PaymentMethodForm'>
      <Formik
        validateOnMount
        validateOnChange
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue }) => {
          const handleAccordionToggle = (eventKey: string) => {
            setCurrentEventKey(eventKey);
            setFieldValue('savedCardId', null);
            dispatch(setPaymentMethodId(null));
          };
          return (
            <>
              <h4 className='green-1'>{text('payByCard')}</h4>
              {!getUserCards.isLoading ? (
                getUserCards.data && getUserCards.data.length > 0 ? (
                  <div>
                    {getUserCards.data?.map((card, i) => (
                      <CardOption
                        key={i}
                        name='savedCardId'
                        card={card}
                        isSelected={card.id === values.savedCardId}
                        onChange={(ev) => {
                          setCurrentEventKey(null);
                          dispatch(setPaymentMethodId(ev.target.value));
                          handleChange(ev);
                        }}
                      />
                    ))}
                    {/* @ts-expect-error: wrong types */}
                    <TextDivider isSecondary>{text('or')}</TextDivider>
                  </div>
                ) : null
              ) : (
                <Loading />
              )}
              {!getUserCards.isLoading && (
                <Accordion
                  defaultActiveKey={
                    getUserCards.data && getUserCards.data.length > 0
                      ? null
                      : 'newCard'
                  }
                  activeKey={currentEventKey}
                >
                  <Accordion.Card
                    toggleLabel={text('addNewCard')}
                    eventKey='newCard'
                    onToggle={handleAccordionToggle}
                  >
                    <NewCard
                      onChange={handleChange}
                      onPaymentElementChange={setIsNewCardDetailsComplete}
                      name='name'
                      value={values.name}
                      label={text('nameOnCard')}
                      isFailed={paymentError?.code === 'incomplete_name'}
                      error={
                        paymentError?.code === 'incomplete_name'
                          ? paymentError.message
                          : ''
                      }
                    />
                    <div className='FormFooter'>
                      <Button
                        onClick={() => handleSubmit(values)}
                        variant='outline-primary'
                        loading={isSubmitting}
                        disabled={isStripeLoading || !isNewCardDetailsComplete}
                      >
                        {text('addCard')}
                      </Button>
                    </div>
                  </Accordion.Card>
                </Accordion>
              )}
              <div className='paymentError'>{paymentError?.message}</div>
              <div>
                {/* @ts-expect-error: wrong types */}
                <Control
                  placeholder='PROMO_CODE'
                  isInputWidthAuto
                  as='input'
                  type='text'
                  value={values.discountCode}
                  className='w-auto'
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('discountCode', event.target.value)
                  }
                  label={text('enterYourPromotionCode')}
                />
              </div>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default PaymentMethodForm;
